import { BRAND } from '@timesheet/shared/brand';
import { cn } from '@/lib/cn';

interface LogoProps {
  /** Visual variant. */
  variant?: 'full' | 'mark';
  /** Color treatment — 'color' = brand red+charcoal, 'mono' = inherits currentColor. */
  tone?: 'color' | 'mono';
  /** Height in px. Width auto-scales. */
  height?: number;
  className?: string;
}

/**
 * SIAM TRUTH AUDIT — Concept A · yin-yang classic.
 *
 * Renders inline SVG so it picks up currentColor in mono mode and avoids
 * an extra HTTP request on first paint.
 */
export function Logo({
  variant = 'full',
  tone = 'color',
  height = 36,
  className,
}: LogoProps) {
  const red = tone === 'color' ? BRAND.colors.red : 'currentColor';
  const dark = tone === 'color' ? BRAND.colors.charcoal : 'currentColor';
  const titleColor = tone === 'color' ? BRAND.colors.charcoal : 'currentColor';
  const subtitleColor = tone === 'color' ? BRAND.colors.grayMid : 'currentColor';
  const subtitleOpacity = tone === 'mono' ? 0.65 : 1;

  if (variant === 'mark') {
    const size = height;
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        role="img"
        aria-label={BRAND.name}
        className={className}
      >
        <title>{BRAND.name}</title>
        <g transform="translate(16, 16)">
          <circle r="14" fill={dark} />
          <path d="M 0,-14 A 14,14 0 0 1 0,14 Z" fill={red} />
          <circle cy="-7" r="7" fill={red} />
          <circle cy="7" r="7" fill={dark} />
          <circle cy="-7" r="1.8" fill={dark} />
          <circle cy="7" r="1.8" fill={red} />
        </g>
      </svg>
    );
  }

  // Full lockup (mark + wordmark)
  const width = (height / 64) * 260;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 260 64"
      role="img"
      aria-label={BRAND.name}
      className={cn('inline-block', className)}
    >
      <title>{BRAND.name}</title>
      <g transform="translate(32, 32)">
        <circle r="28" fill={dark} />
        <path d="M 0,-28 A 28,28 0 0 1 0,28 Z" fill={red} />
        <circle cy="-14" r="14" fill={red} />
        <circle cy="14" r="14" fill={dark} />
        <circle cy="-14" r="3.6" fill={dark} />
        <circle cy="14" r="3.6" fill={red} />
      </g>
      <text
        x="74"
        y="30"
        fontFamily="inherit"
        fontSize="20"
        fontWeight="600"
        fill={titleColor}
        letterSpacing="0.5"
      >
        SIAM TRUTH
      </text>
      <text
        x="74"
        y="50"
        fontFamily="inherit"
        fontSize="11"
        fontWeight="500"
        fill={subtitleColor}
        fillOpacity={subtitleOpacity}
        letterSpacing="4.5"
      >
        AUDIT
      </text>
    </svg>
  );
}
